import { useQuery } from '@tanstack/react-query';
import React, { useEffect, useMemo } from 'react';
import { useOutletContext, useParams } from 'react-router-dom';

import { ROUTE } from '~/app/routes';
import { Details } from '~/components';
import { useNavigate } from '~/lib/navigation';
import { CardTokenRepository } from '~/repositories/CardTokenRepository';
import { useNotificationStore } from '~/stores/Notification';
import { Inner } from '~/styles';

import { CardTokenProps } from './CardToken';

interface Params {
  id: string;
}

const cardTokenQueryKey = (id: string) => ['card-token', id] as const;

export const PageCardTokenApiResponse: React.FC = () => {
  const { addDangerNotification } = useNotificationStore();
  const navigate = useNavigate();
  const { id } = useParams<keyof Params>() as Params;
  const { cardToken: outletCardToken } = useOutletContext<CardTokenProps>();

  const { data: cardToken, error: cardTokenError } = useQuery({
    queryKey: cardTokenQueryKey(id!),
    queryFn: () => CardTokenRepository.get(id!),
    enabled: !!id && !outletCardToken,
    initialData: outletCardToken,
  });

  useEffect(() => {
    if (cardTokenError) {
      navigate(ROUTE.CARD_TOKENS);
      addDangerNotification({
        content: (cardTokenError as any).message || 'Failed to load card token',
      });
    }
  }, [cardTokenError, navigate, addDangerNotification]);

  const formattedCardToken = useMemo(
    () => (cardToken ? (cardToken as unknown as Record<string, unknown>) : undefined),
    [cardToken]
  );

  if (!cardToken) {
    return null;
  }

  return (
    <Inner pt={24} px={0} pb={0}>
      <Details
        label="API Response"
        formattedData={formattedCardToken}
        rawData={cardToken as unknown as Record<string, unknown> | undefined}
      />
    </Inner>
  );
};
